<script setup>
import { useI18n } from 'vue-i18n';
import {
  ticketNeedsResponse,
  ticketStatusMeta,
  waitingOnMeta,
} from '../utils/supportTicketPortal';
import { useSupportTicketFormatters } from '../composables/useSupportTicketFormatters';

const props = defineProps({
  ticket: { type: Object, required: true },
  isSelected: { type: Boolean, default: false },
  priorityOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['select']);

const { t } = useI18n();
const { localizedDate, priorityLabel: optionPriorityLabel } =
  useSupportTicketFormatters();

const priorityLabel = priority =>
  optionPriorityLabel(props.priorityOptions, priority);
</script>

<template>
  <button
    type="button"
    class="grid w-full gap-2 px-5 py-4 text-left transition hover:bg-lais-primary-50/30"
    :class="{
      'bg-white': !isSelected,
      'bg-lais-primary-50/40': isSelected,
    }"
    @click="emit('select')"
  >
    <div class="flex min-w-0 items-start justify-between gap-3">
      <div class="min-w-0">
        <p class="truncate text-[14px] font-semibold text-lais-text">
          {{ ticket.subject }}
        </p>
        <p class="mt-1 truncate text-[12px] text-lais-text-muted">
          {{ ticket.requester_name || ticket.requester_email }}
        </p>
      </div>
      <span
        class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
        :class="ticketStatusMeta(ticket.product_status, { t }).className"
      >
        {{ ticketStatusMeta(ticket.product_status, { t }).label }}
      </span>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <span
        class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
        :class="waitingOnMeta(ticket.waiting_on, { t }).className"
      >
        {{ waitingOnMeta(ticket.waiting_on, { t }).label }}
      </span>
      <span
        v-if="ticketNeedsResponse(ticket)"
        class="rounded-full bg-rose-600 px-2 py-0.5 text-[11px] font-semibold text-white"
      >
        {{ t('SUPPORT_TICKETS.LIST.AWAITING_REPLY') }}
      </span>
      <span
        v-if="['high', 'urgent'].includes(ticket.sla?.priority)"
        class="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
      >
        {{ priorityLabel(ticket.sla?.priority) }}
      </span>
      <span
        v-if="ticket.sla?.sla_status === 'breached'"
        class="rounded-full bg-rose-600/15 px-2 py-0.5 text-[11px] font-semibold text-rose-700"
      >
        {{ t('SUPPORT_TICKETS.SLA.STATUS.BREACHED') }}
      </span>
      <span class="text-[11px] text-lais-text-muted">
        {{ localizedDate(ticket.updated_at) }}
      </span>
    </div>
  </button>
</template>
